import { memo, useState } from 'react';

import {
  AdornedInput,
  Box,
  Button,
  Divider,
  Form,
  FormField,
  FormHelp,
  Label,
  Select,
  Text,
} from '@zillow/constellation-10';
import { IconDeleteOutline } from '@zillow/constellation-icons';
import { CharacterCountFormHelp } from '@zg-rentals/visual-cohesion';

import { bathOptions, bedOptions } from '../../../../../../../srcLegacy/shared/constants/ListingDetailsConstants';

type UnitOverviewProps = {
  index: number;
  unitNumber: string;
  bedrooms: string;
  baths: string;
  hasDuplicateUnitNumber?: boolean;
  hasEmptyUnitNumber?: boolean;
  hasMaxCharactersUnitNumber?: boolean;
  disableDeleteButton?: boolean;
  onChange: (index: string, name: string, value: string) => void;
  onDelete: (index: string) => void;
};

/* description box to avoid layout shift when unit number has an error */
const DescriptionBox = () => <Box css={{ height: '21.5px' }} />;

/**
 * Controlled component for unit overview form fields.
 * Validation is handled by the parent component.
 */
const UnitOverview = ({
  onChange,
  onDelete,
  index,
  unitNumber,
  bedrooms,
  baths,
  hasDuplicateUnitNumber = false,
  hasEmptyUnitNumber = false,
  hasMaxCharactersUnitNumber = false,
  disableDeleteButton = false,
}: UnitOverviewProps) => {
  const [isFocused, setIsFocused] = useState(false);
  const MAX_LENGTH = 20;
  const isOverLimit = unitNumber.length > MAX_LENGTH;

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>) => {
    const { value, name } = e.target;
    onChange(index.toString(), name, value);
  };

  const handleDelete = () => {
    onDelete(index.toString());
  };

  return (
    <Box
      data-testid="UnitOverview"
      css={{
        padding: '300',
      }}
    >
      <Form
        data-testid="UnitOverview-Form"
        css={{
          display: 'flex',
          flexDirection: 'row',
          gap: '400',
          justifyContent: 'space-between',
          alignItems: 'flex-start',
        }}
      >
        <FormField
          control={
            <AdornedInput
              startAdornment={<AdornedInput.Adornment>#</AdornedInput.Adornment>}
              input={
                <AdornedInput.Input
                  name="unitNumber"
                  value={unitNumber}
                  onChange={handleChange}
                  onFocus={() => setIsFocused(true)}
                  onBlur={() => setIsFocused(false)}
                />
              }
            />
          }
          label={<Label>Unit number</Label>}
          description={
            hasEmptyUnitNumber ? (
              <FormHelp>Unit number cannot be empty</FormHelp>
            ) : hasDuplicateUnitNumber ? (
              <FormHelp>This unit already exists</FormHelp>
            ) : hasMaxCharactersUnitNumber || isFocused || isOverLimit ? (
              <CharacterCountFormHelp
                currentLength={unitNumber.length}
                maxLength={MAX_LENGTH}
                isFocused={isFocused}
                label="Unit number"
                helperText="Enter unit number (e.g., 1A, 201)"
              />
            ) : (
              <DescriptionBox />
            )
          }
          error={hasEmptyUnitNumber || hasDuplicateUnitNumber || hasMaxCharactersUnitNumber || isOverLimit}
          required={true}
          css={{ flex: 1 }}
        />

        <FormField
          control={
            <Select name="bedrooms" value={bedrooms} onChange={handleChange}>
              {bedOptions.map((option: string) => (
                <option key={option} value={option}>
                  {option || 'Select'}
                </option>
              ))}
            </Select>
          }
          label={
            <Label>
              Bedrooms{' '}
              <Text textStyle="body-sm" color="text.subtle">
                (optional)
              </Text>
            </Label>
          }
          description={<DescriptionBox />}
          required={false}
          css={{ flex: 1 }}
        />

        <FormField
          control={
            <Select name="baths" value={baths} onChange={handleChange}>
              {bathOptions.map((option: string) => (
                <option key={option} value={option}>
                  {option || 'Select'}
                </option>
              ))}
            </Select>
          }
          label={
            <Label>
              Bathrooms{' '}
              <Text textStyle="body-sm" color="text.subtle">
                (optional)
              </Text>
            </Label>
          }
          description={<DescriptionBox />}
          required={false}
          css={{ flex: 1 }}
        />

        {/* add margin bottom to hold align the delete button when the unit number has an error */}
        <Box data-testid="DeleteButton-Container" css={{ marginBottom: '200' }}>
          <Button.Icon
            data-testid="DeleteButton"
            title="Delete unit"
            size="md"
            onClick={handleDelete}
            css={{
              cursor: disableDeleteButton ? 'not-allowed' : 'pointer',
              opacity: disableDeleteButton ? 0.5 : 1,
              pointerEvents: disableDeleteButton ? 'none' : 'auto',
            }}
          >
            <IconDeleteOutline />
          </Button.Icon>
        </Box>
      </Form>

      <Divider orientation="horizontal" role="separator" tone="muted" />
    </Box>
  );
};

export default memo(UnitOverview);
